import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Megaphone,
    Clock,
    User,
    CheckCircle2,
    Bell,
    Check,
    Calendar,
    ArrowRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import { useUnreadAnnouncements } from '../../hooks/useUnreadAnnouncements';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

const UserAnnouncementsPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const dynamicGradient = useDynamicGradient();

    // Use hook for data
    const { announcements } = useUnreadAnnouncements();

    const handleMarkAsRead = async (id) => {
        if (!user) return;
        const userId = user.discordId || user.id;

        try {
            const { error } = await supabase
                .from('announcement_reads')
                .insert([
                    { announcement_id: id, user_id: String(userId) }
                ]);

            if (error) {
                console.error('Error marking as read:', error);
                toast.error('Erro ao marcar como lido.');
            } else {
                toast.success('Marcado como lido!');
                // Hook will auto-update via realtime
            }
        } catch (err) {
            console.error('Error:', err);
        }
    };

    const getAnnouncementStyles = () => {
        return {
            gradient: 'from-blue-600/10 via-purple-600/10 to-pink-600/10',
            border: 'border-white/10',
            badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
            icon: 'text-purple-400'
        };
    };

    // Announcements are already filtered and sorted by the hook
    const visibleAnnouncements = announcements;

    return (
        <div className="w-full min-h-full bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white p-6 rounded-3xl pb-20">
            {/* Header Moderno */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-10"
            >
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-4xl font-bold flex items-center gap-3">
                            <Megaphone className="h-10 w-10 text-purple-400" />
                            <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                                Comunicados Oficiais
                            </span>
                        </h1>
                        <p className="text-gray-400 mt-2 text-lg">
                            Fique por dentro das novidades e avisos importantes
                        </p>
                    </div>

                    <div className="hidden md:block text-right bg-gradient-to-r from-black/40 to-black/20 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
                        <div className="flex items-center justify-end gap-2 text-purple-300 mb-1">
                            <Bell className="w-4 h-4" />
                            <span className="text-sm font-medium">
                                {visibleAnnouncements.filter(a => !a.isRead).length} Não Lidos
                            </span>
                        </div>
                        <p className="text-xs text-gray-500">Total de Avisos: {visibleAnnouncements.length}</p>
                    </div>
                </div>
            </motion.div>

            {/* Lista de Comunicados */}
            <div className="space-y-6">
                {visibleAnnouncements.length > 0 ? (
                    visibleAnnouncements.map((announcement, index) => {
                        const isRead = announcement.isRead;
                        const styles = getAnnouncementStyles();

                        return (
                            <motion.div
                                key={announcement.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={{ scale: 1.01, x: 5 }}
                            >
                                <Card className={`bg-gradient-to-br ${styles.gradient} backdrop-blur-sm border ${styles.border} overflow-hidden transition-all duration-300 shadow-lg group`}>
                                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                                        <Megaphone className={`w-32 h-32 ${styles.icon}`} />
                                    </div>

                                    <CardContent className="p-6 relative z-10">
                                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                            <div className="flex-1 space-y-4">
                                                <div className="flex items-center flex-wrap gap-3">
                                                    {!isRead && (
                                                        <span className="relative flex h-3 w-3">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                                                        </span>
                                                    )}
                                                    <h3 className="text-2xl font-bold text-white group-hover:text-purple-200 transition-colors">
                                                        {announcement.title}
                                                    </h3>

                                                    {!isRead && (
                                                        <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 animate-pulse border">
                                                            Novo
                                                        </Badge>
                                                    )}
                                                </div>

                                                <div className="prose prose-invert max-w-none">
                                                    <p className="text-gray-200 whitespace-pre-wrap leading-relaxed text-base">
                                                        {announcement.content}
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-6 text-sm text-gray-400 pt-2 border-t border-white/5 mt-4">

                                                    <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-full">
                                                        <Calendar className="h-4 w-4 text-gray-300" />
                                                        <span>{new Date(announcement.created_at || announcement.createdAt).toLocaleDateString('pt-BR')}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-full">
                                                        <Clock className="h-4 w-4 text-gray-300" />
                                                        <span>{new Date(announcement.created_at || announcement.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-full">
                                                        <User className="h-4 w-4 text-gray-300" />
                                                        <span>{announcement.author}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col justify-between items-end gap-4 min-w-[140px]">
                                                {announcement.ticket_id && (
                                                    <Button
                                                        onClick={() => navigate(`/tickets/${announcement.ticket_id}`)}
                                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all"
                                                    >
                                                        <ArrowRight className="h-4 w-4 mr-2" />
                                                        Ver Ticket
                                                    </Button>
                                                )}
                                                {!isRead ? (
                                                    <Button
                                                        onClick={() => handleMarkAsRead(announcement.id)}
                                                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-900/20 group-hover:shadow-purple-900/40 transition-all"
                                                    >
                                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                                        Marcar como Lido
                                                    </Button>
                                                ) : (
                                                    <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-4 py-2 rounded-lg border border-green-500/20 w-full justify-center">
                                                        <Check className="h-4 w-4" />
                                                        <span>Lido em {
                                                            announcement.readAt
                                                                ? new Date(announcement.readAt).toLocaleDateString()
                                                                : 'Hoje'
                                                        }</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })
                ) : (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Card className="bg-gradient-to-br from-gray-900/60 to-gray-800/60 backdrop-blur-md border border-gray-700/50">
                            <CardContent className="p-16 text-center flex flex-col items-center">
                                <div className="bg-gray-800/50 p-6 rounded-full mb-6">
                                    <Megaphone className="h-16 w-16 text-gray-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-3">Tudo Silencioso por Aqui</h3>
                                <p className="text-gray-400 max-w-md mx-auto text-lg">
                                    Não há comunicados oficiais no momento.
                                    <br />Fique tranquilo, nós avisaremos quando houver novidades!
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default UserAnnouncementsPage;
